import { geoApi } from '@api'

import { CheckoutCurrency } from '@redux/types/home/PaymentStateType'

export type DisplayCurrencyValue = CheckoutCurrency

const EVENT_NAME = 'judahs-display-currency-change'

/** 방문자가 푸터에서 직접 고른 통화. 없으면 판정값을 따른다 */
const CHOICE_KEY = 'judahs_currency'
/** 이번 세션에 서버가 알려 준 국가. 탭을 닫으면 사라진다 — 다음 방문에 다시 묻는다 */
const COUNTRY_KEY = 'judahs_geo_country'

const isCurrency = (value: string | null): value is DisplayCurrencyValue =>
	value === 'USD' || value === 'KRW'

const readChoice = (): DisplayCurrencyValue | null => {
	try {
		const stored = window.localStorage.getItem(CHOICE_KEY)
		return isCurrency(stored) ? stored : null
	} catch {
		return null
	}
}

const readCountry = (): string | null => {
	try {
		return window.sessionStorage.getItem(COUNTRY_KEY)
	} catch {
		return null
	}
}

const writeCountry = (country: string) => {
	try {
		window.sessionStorage.setItem(COUNTRY_KEY, country)
	} catch {
		// 프라이빗 모드 등 — 캐시 없이 간다
	}
}

/**
 * 프리렌더(`scripts/prerender.js`)는 이 Mac 에서 돈다 — 타임존이 Asia/Seoul 이고 IP 도 한국이라
 * 그대로 두면 구운 HTML 에 원화가 박힌다. 크롤러가 읽는 정본은 USD 다. 스크립트가 플래그를 세운다.
 */
const isPrerender = (): boolean =>
	typeof window !== 'undefined' && (window as any).__JUDAHS_PRERENDER__ === true

/** 서버 응답 전의 추정 — 기기 타임존이 서울이면 한국에 있는 사람이다 (VPN 은 어쩔 수 없다) */
const guessByTimezone = (): DisplayCurrencyValue => {
	try {
		const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
		return zone === 'Asia/Seoul' ? 'KRW' : 'USD'
	} catch {
		return 'USD'
	}
}

const fromCountry = (country: string): DisplayCurrencyValue =>
	country === 'KR' ? 'KRW' : 'USD'

const notify = () => window.dispatchEvent(new CustomEvent(EVENT_NAME))

let detecting: Promise<void> | null = null

/**
 * 표시 통화 (PRICING.md §6 · §7.3, 2026-09-16 사용자 결정).
 *
 * 한국에서 온 방문자에게는 `/shop` · `/shop/{code}` · `/products/pls8` · `/cart` 가 **원화만** 보여 준다.
 * 달러를 작게라도 같이 두지 않는다 — $1,000 이 준거로 먼저 박히면 ₩1,500,000 은 "13만 6천 원 더 받는
 * 값"으로 읽힌다(§7.3). 그 밖의 방문자는 지금처럼 USD 만.
 *
 * 판정 순서: ① 푸터에서 직접 고른 값 → ② 이번 세션에 서버(`GET /geo`, geoip)가 알려 준 국가 →
 * ③ 응답 전에는 기기 타임존. 결제 통화는 여기가 아니라 **배송국**이 정한다(`payment.saga` currencyOf) —
 * 이 값은 표시만 바꾼다. 원화 값이 없는 상품(`krw <= 0`)은 화면이 USD 로 되돌린다.
 */
export const DisplayCurrency = {
	get: (): DisplayCurrencyValue => {
		if (isPrerender()) return 'USD'
		const choice = readChoice()
		if (choice !== null) return choice
		const country = readCountry()
		// 서버가 판정을 못 했거나(UNKNOWN — localhost · 옛 API) 아직 안 물었으면 타임존으로 추정한다
		if (country !== null && country !== 'UNKNOWN') return fromCountry(country)
		return guessByTimezone()
	},

	/** 방문자가 직접 고른 값이 있는가 — 있으면 서버 판정을 덮지 않는다 */
	hasChoice: (): boolean => readChoice() !== null,

	/**
	 * 서버가 이번 세션에 알려 준 국가 코드. **확정된 것만** 돌려준다 — 아직 안 물었거나
	 * 판정을 못 했으면(`UNKNOWN`) `null` 이다. 읽는 쪽은 `null` 을 "모른다" 로 다루고
	 * 안전한 쪽(= 더 많이 보여 주는 쪽)으로 넘어져야 한다. 표시 통화와 달리 이 값은
	 * 방문자가 되돌릴 수단이 없기 때문이다.
	 */
	country: (): string | null => {
		const country = readCountry()
		return country === null || country === 'UNKNOWN' ? null : country
	},

	/** 서버에 국가를 한 번 묻는다. 세션에 답이 있거나 직접 고른 값이 있으면 묻지 않는다 */
	detect: (): Promise<void> => {
		if (isPrerender() || readChoice() !== null || readCountry() !== null) {
			return Promise.resolve()
		}
		if (detecting !== null) return detecting

		detecting = (async () => {
			const res = await geoApi.getGeo()
			const country: string =
				res !== undefined && [200, 201].includes(res.status) && Array.isArray(res.data)
					? String(res.data[0]?.country ?? 'UNKNOWN')
					: 'UNKNOWN'
			writeCountry(country)
			/*
				통화가 그대로여도 알린다. 국가만 보는 구독자가 있다 — 구매 패널의 착지 원가 행이
				미국 방문자에게 VAT 줄을 감춘다(PRICING §8.3 예외, 2026-09-21). 통화 구독자는
				같은 값으로 setState 하므로 리렌더가 늘지 않는다.
			*/
			notify()
		})().finally(() => {
			detecting = null
		})

		return detecting
	},

	/** 푸터의 USD / KRW. 고른 값은 다음 방문에도 남는다 */
	set: (value: DisplayCurrencyValue): void => {
		try {
			window.localStorage.setItem(CHOICE_KEY, value)
		} catch {
			// 저장이 안 되면 이번 화면에서만 바뀐다
		}
		notify()
	},

	/** 값이 바뀌면 호출된다. 해제 함수를 반환한다 */
	subscribe: (listener: (value: DisplayCurrencyValue) => void): (() => void) => {
		const handler = () => listener(DisplayCurrency.get())
		window.addEventListener(EVENT_NAME, handler)
		return () => window.removeEventListener(EVENT_NAME, handler)
	},
}
